// import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { usePageTitle } from 'utils/pageTitle';
import DashboardLayout from 'pages/_rd_dashboard/DashboardLayout';

import { getTotalPortfolioViolations } from 'reducers/propertyListReducer';
import {
	getFavoriteProperties,
	getPortfolioProperties,
	getPortfolioPropertiesCount,
} from 'data/reducers/propertyManagementReducer';

import AlertsAndNotificationsTable from 'pages/_rd_dashboard/AlertsAndNotificationsTable';
import PortfolioMapWidget from 'pages/_rd_dashboard/PortfolioMapWidget';
import PortfolioCalendar from 'pages/_rd_dashboard/PortfolioCalendar';
import PropertyCard from 'view/components/PropertyCard';
import Spinner from 'components/Spinner';
// import Paginator from 'components/Paginator';

import './DashboardOverview.css';
import { memo } from 'react';
import { SubscriptionUsageSummary } from 'view/components/Subscription';
import { ReactComponent as Building2Icon } from 'assets/icons/building-2.svg';

// VWA-2818: fixed 150px height, centered content - matches Figma frame
// 31:662 node #31:1371/#31:1372 (template EL-318fad45) exactly.
const STAT_CARD_CLASSES =
	'violerts-v3-root tw-flex tw-flex-1 tw-flex-col tw-gap-1 tw-rounded-[8px] tw-border tw-border-border-01 tw-bg-surface-layer-01 tw-px-6 tw-py-4';

const DashboardInfoCard = memo(({ title, Icon, content, isLoading }) => {
	return (
		<div className={STAT_CARD_CLASSES}>
			<div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-text-primary">
				{typeof Icon === 'string' ? (
					<i className={`${Icon} tw-text-base tw-text-text-primary`} aria-hidden="true" />
				) : (
					<Icon className="tw-h-4 tw-w-4 tw-text-text-primary" aria-hidden="true" />
				)}
				<span>{title}</span>
			</div>
			<div className="tw-text-3xl tw-font-bold tw-text-text-primary">
				{isLoading ? <Spinner /> : content ?? '-'}
			</div>
		</div>
	);
});

const DashboardOverview = (props) => {
	usePageTitle('Dashboard');
	const dispatch = useDispatch();
	const history = useHistory();
	const sliderRef = useRef(null);
	const [currentSlide, setCurrentSlide] = useState(0);

	let {
		userData,
		isLoadingTotalPortfolioViolations,
		// propertyList,
		totalViolations,
	} = useSelector((state) => ({
		userData: state.user.userData,
		isLoadingTotalPortfolioViolations: state.propertyList.isLoadingViolations,
		propertyList: state.propertyList?.propertyList,
		totalViolations: state.propertyList?.totalViolations,
	}));

	// Get portfolio properties from propertyManagement reducer
	const {
		favoriteProperties,
		isLoadingFavoriteProperties,
		favoritePropertiesError,
		portfolioPropertiesCount,
		isLoadingPortfolioPropertiesCount,
	} = useSelector((state) => state.propertyManagement);

	// "Properties Saved" count comes from the lightweight count endpoint
	// (org/role-scoped) rather than the length of the full properties array.
	const totalProperties =
		typeof portfolioPropertiesCount === 'number'
			? portfolioPropertiesCount
			: '-';

	// Calculate slider properties
	const itemsPerSlide = 4;
	const totalSlides = Math.ceil(
		(favoriteProperties?.length || 0) / itemsPerSlide,
	);
	const canSlideLeft = currentSlide > 0;
	const canSlideRight = currentSlide < totalSlides - 1;

	useEffect(() => {
		if (userData?.id) {
			dispatch(getTotalPortfolioViolations(userData.id));
			dispatch(getFavoriteProperties());
			dispatch(getPortfolioPropertiesCount());
			// Full property array still needed for the Portfolio Map widget.
			dispatch(getPortfolioProperties());
		}
	}, [dispatch, userData]);

	// Slider handlers
	const slideLeft = () => {
		if (canSlideLeft) {
			setCurrentSlide(currentSlide - 1);
		}
	};

	const slideRight = () => {
		if (canSlideRight) {
			setCurrentSlide(currentSlide + 1);
		}
	};

	// Property handlers
	const handlePropertyClick = (property) => {
		history.push(`/property/${property.address}/overview`);
	};

	const handlePropertyNotificationClick = (property) => {
		// Navigate to property notifications
		history.push(`/property/${property.address}/notifications`);
	};

	const handlePropertyMenuClick = (property) => {
		// Handle property menu actions
		console.log('Property menu clicked for:', property);
	};

	return (
		<DashboardLayout>
			<div className="dashboard-overview">
				<h1 className="violerts-v3-root !tw-ml-0 tw-mb-5 tw-text-2xl tw-font-bold tw-text-text-primary">
					Welcome back {userData?.name || ''}
				</h1>
				<div className="violerts-v3-root tw-mb-6 tw-flex tw-flex-col tw-gap-3">
					<h2 className="tw-text-base tw-font-bold tw-text-text-primary">Overview</h2>
					<div className="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row">
						<DashboardInfoCard
							title="Properties Saved"
							Icon={Building2Icon}
							content={totalProperties}
							isLoading={isLoadingPortfolioPropertiesCount}
						/>
						<DashboardInfoCard
							title="Open Violations"
							Icon="bi bi-clock"
							content={
								typeof totalViolations?.total === 'undefined'
									? '-'
									: totalViolations.total
							}
							isLoading={isLoadingTotalPortfolioViolations}
						/>
					</div>
				</div>
				<div className="tw-mb-6">
					<SubscriptionUsageSummary />
				</div>
				<div className="violerts-v3-root tw-mb-6">
					<h2 className="tw-text-lg tw-font-bold tw-text-text-primary tw-mb-3">Portfolio Map</h2>
					<PortfolioMapWidget />
				</div>
				<div className="violerts-v3-root tw-mb-6">
					<h2 className="tw-text-lg tw-font-bold tw-text-text-primary tw-mb-3">Favorite Properties</h2>
					<div className="favorites-section">
						{isLoadingFavoriteProperties ? (
							<div className="favorites-loading">
								<Spinner />
							</div>
						) : favoritePropertiesError ? (
							<div className="favorites-error">
								Error loading favorite properties
							</div>
						) : favoriteProperties && favoriteProperties.length > 0 ? (
							<div className="favorites-slider-container">
								{/* Left Slider Button */}
								{canSlideLeft && (
									<button
										className="slider-button slider-button-left"
										onClick={slideLeft}
										type="button"
									>
										<i className="bi bi-chevron-left" />
									</button>
								)}
								{/* Slider Content */}
								<div className="favorites-slider" ref={sliderRef}>
									<div
										className="favorites-slider-track"
										style={{
											transform: `translateX(-${currentSlide * 100}%)`,
										}}
									>
										{Array.from({ length: totalSlides }).map(
											(_, slideIndex) => (
												<div key={slideIndex} className="favorites-slide">
													{favoriteProperties
														.slice(
															slideIndex * itemsPerSlide,
															(slideIndex + 1) * itemsPerSlide,
														)
														.map((property) => (
															<PropertyCard
																key={property.id}
																property={property}
																onClick={() => handlePropertyClick(property)}
																onNotificationClick={() =>
																	handlePropertyNotificationClick(property)
																}
																onMenuClick={() =>
																	handlePropertyMenuClick(property)
																}
																showNotificationIcon={false}
																showDeleteButton={false}
																showMenuButton={false}
															/>
														))}
												</div>
											),
										)}
									</div>
								</div>
								{/* Right Slider Button */}
								{canSlideRight && (
									<button
										className="slider-button slider-button-right"
										onClick={slideRight}
										type="button"
									>
										<i className="bi bi-chevron-right" />
									</button>
								)}
							</div>
						) : (
							<div className="favorites-empty">
								No favorite properties found
							</div>
						)}
					</div>
				</div>
				<div className="violerts-v3-root tw-mb-6">
					<AlertsAndNotificationsTable title="Recent Alerts & Notifications" />
				</div>
				<div className="violerts-v3-root tw-mb-6">
					<h2 className="tw-text-lg tw-font-bold tw-text-text-primary tw-mb-3">Calendar</h2>
					<div className="calendar-section">
						<PortfolioCalendar />
					</div>
				</div>
			</div>
		</DashboardLayout>
	);
};

export default DashboardOverview;
